import { Ionicons } from '@expo/vector-icons';
import * as Location from 'expo-location';
import React, { useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import MaskInput from 'react-native-mask-input';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { useProviderProfile } from '@/hooks/use-provider';
import {
  fetchAddressByCep,
  isValidCep,
  parseAddress,
} from '@/services/cepService';
import { ReverseAddress, reverseGeocode } from '@/services/geocodeService';
import { MapPickerSheet } from './map-picker-sheet';
import { AddressWidgetData, WidgetProps } from './widget-types';
import { WidgetCard } from './widget-card';
import { getApiErrorMessage } from '@/utils/api-error';
import { useThemedHex } from '@/hooks/use-theme-colors';

const CEP_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/];
const PRIMARY = '#FD5001';
const MUTED = '#9ca3af';

/** Os campos do endereço, sempre editáveis venham de onde vierem. */
interface AddressFields {
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const EMPTY: AddressFields = {
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

/** O endereço numa linha só, que é como o serviço e o perfil guardam. */
function compose(fields: AddressFields): string {
  const street = [fields.street, fields.number.trim()]
    .filter(Boolean)
    .join(', ');
  const withComplement = [street, fields.complement.trim()]
    .filter(Boolean)
    .join(' - ');
  const city = [fields.city, fields.state].filter(Boolean).join(' - ');
  const cep = fields.cep.trim() ? `CEP ${fields.cep.trim()}` : '';

  return [withComplement, fields.neighborhood, city, cep]
    .filter(Boolean)
    .join(', ');
}

/**
 * O endereço de quem recebe cliente.
 *
 * Três caminhos para o mesmo lugar, e **todos caem nos mesmos campos
 * editáveis**: o endereço do cadastro, o GPS ("estou na loja agora") e a busca
 * por CEP. O que qualquer um deles reconhecer, ele corrige por cima — nenhum
 * é trilho.
 *
 * Por cima disso, o mapa: é lá que ele marca o ponto exato, que é o que o
 * texto nunca acerta (galpão sem número, sala nos fundos, rua que o CEP cobre
 * inteira). O ponto vai junto com o endereço e vira a coordenada do
 * estabelecimento.
 */
export function AddressWidget({
  data,
  editable,
  onAdvance,
}: WidgetProps<AddressWidgetData>) {
  const th = useThemedHex();
  const patch = usePatchDraft();
  const { data: provider } = useProviderProfile();
  const profileAddress = provider?.address?.trim() || '';

  // Reabrir o cartão precisa mostrar o endereço em campos, não numa linha
  // solta: é o mesmo motivo do atalho do perfil.
  const [fields, setFields] = useState<AddressFields>(() => {
    if (!data.address) return EMPTY;
    const parsed = parseAddress(data.address);
    return parsed.street || parsed.city ? parsed : EMPTY;
  });
  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(
    data.latitude != null && data.longitude != null
      ? { lat: data.latitude, lon: data.longitude }
      : null,
  );
  /** O endereço que veio pronto (perfil, GPS sem rua, mapa) e ele não digitou. */
  /** O endereço que veio pronto e não deu para separar em campos. */
  const [freeform, setFreeform] = useState(() => {
    if (!data.address) return '';
    const parsed = parseAddress(data.address);
    return parsed.street || parsed.city ? '' : data.address;
  });
  const [busy, setBusy] = useState<'cep' | 'gps' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key: keyof AddressFields, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setFreeform('');
  };

  /**
   * Uma linha pronta (o endereço do cadastro) vira campos editáveis.
   *
   * Sem isto o atalho preenchia só o texto invisível e a tela ficava com todos
   * os campos vazios e o botão de salvar ativo — parecia que não tinha
   * funcionado, e o que salvava não era o que ele estava vendo.
   */
  const adopt = (line: string) => {
    const parsed = parseAddress(line);
    if (parsed.street || parsed.city) {
      setFields({ ...parsed, complement: parsed.complement });
      setFreeform('');
      return;
    }
    // Linha que não dá para separar continua valendo inteira.
    setFreeform(line);
  };

  const typed = compose(fields);
  const address = typed || freeform;
  const canEdit = editable && !done;

  const searchCep = async () => {
    if (!isValidCep(fields.cep)) {
      setError('Informe um CEP com 8 dígitos.');
      return;
    }
    setBusy('cep');
    setError(null);
    try {
      const found = await fetchAddressByCep(fields.cep);
      setFields(prev => ({
        ...prev,
        street: found.street,
        neighborhood: found.neighborhood,
        city: found.city,
        state: found.state,
      }));
      setFreeform('');
    } catch (failure) {
      setError(
        getApiErrorMessage(failure, 'Não consegui buscar o CEP agora.'),
      );
    } finally {
      setBusy(null);
    }
  };

  /**
   * "Estou na loja agora": o aparelho já sabe onde é.
   *
   * O reverso do sistema operacional resolve rua e bairro no celular, mas **na
   * web ele não existe** — saiu do SDK 49 e o console só avisa para usar a API
   * paga do Google. Por isso a coordenada cai no mesmo serviço público que o
   * mapa usa quando o nativo não responde: o botão preenche em todo lugar.
   *
   * O número é o que o GPS mais erra; os campos ficam editáveis por isso.
   */
  const locateMe = async () => {
    setBusy('gps');
    setError(null);
    try {
      const permission = await Location.requestForegroundPermissionsAsync();
      if (!permission.granted) {
        setError('Sem permissão de localização. Busque pelo CEP.');
        return;
      }

      const position = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });
      const { latitude, longitude } = position.coords;
      setPoint({ lat: latitude, lon: longitude });

      const place = await describe(latitude, longitude);
      if (!place) {
        setError(
          'Peguei sua localização, mas não achei o endereço. Complete abaixo ou busque pelo CEP.',
        );
        return;
      }

      setFields(prev => ({
        ...prev,
        cep: place.cep || prev.cep,
        street: place.street || prev.street,
        number: place.number || prev.number,
        neighborhood: place.neighborhood || prev.neighborhood,
        city: place.city || prev.city,
        state: place.state || prev.state,
      }));
      setFreeform('');
    } catch {
      setError('Não consegui pegar sua localização. Busque pelo CEP.');
    } finally {
      setBusy(null);
    }
  };

  const save = async (value: string | null) => {
    if (done) return;
    setDone(true);
    await patch.mutateAsync({
      payload: {
        location: {
          address: value,
          latitude: value ? (point?.lat ?? null) : null,
          longitude: value ? (point?.lon ?? null) : null,
        },
      },
    });
    onAdvance?.(value ?? 'Prefiro não mostrar o endereço.');
  };

  return (
    <>
      <WidgetCard title={data.title} icon="location-outline">
        {data.hint ? (
          <ThemedText className="text-xs text-foreground/45">
            {data.hint}
          </ThemedText>
        ) : null}

        {!canEdit ? (
          address ? (
            <ThemedText className="text-sm text-foreground/70">
              {address}
            </ThemedText>
          ) : null
        ) : (
          <>
            <View className="flex-row gap-2">
              <Shortcut
                icon="navigate-outline"
                label={busy === 'gps' ? 'Localizando...' : 'Estou aqui agora'}
                onPress={() => void locateMe()}
                disabled={busy !== null}
              />
              <Shortcut
                icon="map-outline"
                label="Marcar no mapa"
                onPress={() => setMapOpen(true)}
                disabled={busy !== null}
              />
            </View>

            {profileAddress && !address ? (
              <PressableScale
                onPress={() => adopt(profileAddress)}
                className="flex-row items-center gap-2 rounded-xl border border-border/60 px-3 py-2.5"
              >
                <Ionicons name="home-outline" size={16} color={PRIMARY} />
                <View className="flex-1">
                  <ThemedText className="text-xs font-semibold text-primary">
                    Usar o endereço do meu cadastro
                  </ThemedText>
                  <ThemedText className="text-xs text-foreground/50">
                    {profileAddress}
                  </ThemedText>
                </View>
              </PressableScale>
            ) : null}

            <View className="gap-2">
              <ThemedText className="text-xs text-foreground/45">CEP</ThemedText>
              <View className="flex-row items-center gap-2">
                <View className="flex-1 rounded-xl border border-border/60 px-3 py-2.5">
                  <MaskInput
                    value={fields.cep}
                    onChangeText={masked => {
                      set('cep', masked);
                      setError(null);
                    }}
                    mask={CEP_MASK}
                    keyboardType="number-pad"
                    placeholder="00000-000"
                    placeholderTextColor={MUTED}
                    style={{ fontSize: 14, color: th('#171717') }}
                  />
                </View>
                <PressableScale
                  onPress={() => void searchCep()}
                  disabled={busy !== null}
                  className="items-center justify-center rounded-xl bg-primary px-4 py-3"
                >
                  {busy === 'cep' ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Ionicons name="search" size={16} color="#fff" />
                  )}
                </PressableScale>
              </View>
            </View>

            {/* Sempre editáveis: o que o GPS, o CEP ou o mapa reconhecerem
                entra aqui e ele corrige por cima. */}
            <Field
              label="Rua"
              value={fields.street}
              onChange={value => set('street', value)}
              placeholder="Rua das Flores"
            />
            <View className="flex-row gap-2">
              <View className="flex-1">
                <Field
                  label="Número"
                  value={fields.number}
                  onChange={value => set('number', value)}
                  placeholder="120"
                  keyboardType="number-pad"
                />
              </View>
              <View className="flex-1">
                <Field
                  label="Complemento"
                  value={fields.complement}
                  onChange={value => set('complement', value)}
                  placeholder="Sala 2"
                />
              </View>
            </View>
            <View className="flex-row gap-2">
              <View className="flex-1">
                <Field
                  label="Bairro"
                  value={fields.neighborhood}
                  onChange={value => set('neighborhood', value)}
                  placeholder="Centro"
                />
              </View>
              <View className="flex-1">
                <Field
                  label="Cidade"
                  value={fields.city}
                  onChange={value => set('city', value)}
                  placeholder="Contagem"
                />
              </View>
            </View>

            {error ? (
              <ThemedText className="text-xs text-error-600">
                {error}
              </ThemedText>
            ) : null}

            {point ? (
              <View className="flex-row items-center gap-2">
                <Ionicons name="pin" size={13} color={PRIMARY} />
                <ThemedText className="text-xs text-foreground/55">
                  Ponto marcado no mapa
                </ThemedText>
              </View>
            ) : null}

            {address ? (
              <ThemedText className="text-xs text-foreground/60">
                {address}
              </ThemedText>
            ) : null}

            <View className="gap-2">
              <PressableScale
                onPress={() => void save(address || null)}
                disabled={patch.isPending || !address}
                className={`items-center rounded-full py-2.5 ${
                  address ? 'bg-primary' : 'bg-neutral-200'
                }`}
              >
                <ThemedText className="text-sm font-semibold text-white">
                  {patch.isPending ? 'Salvando...' : data.confirmLabel}
                </ThemedText>
              </PressableScale>
              {data.skipLabel ? (
                <PressableScale
                  onPress={() => void save(null)}
                  className="items-center py-1"
                >
                  <ThemedText className="text-xs font-semibold text-foreground/45">
                    {data.skipLabel}
                  </ThemedText>
                </PressableScale>
              ) : null}
            </View>
          </>
        )}
      </WidgetCard>

      <MapPickerSheet
        open={mapOpen}
        latitude={point?.lat}
        longitude={point?.lon}
        // O mapa abre procurando o negócio pelo nome: é assim que ele acha o
        // próprio ponto quando o estabelecimento já está no mapa.
        query={address || data.businessName || provider?.title || ''}
        onPick={place => {
          setPoint({ lat: place.latitude, lon: place.longitude });
          setMapOpen(false);
          // O ponto do mapa também preenche os campos: o alfinete diz onde é,
          // e ele ainda precisa conferir número e complemento.
          void describe(place.latitude, place.longitude).then(found => {
            if (found) {
              setFields(prev => ({
                cep: found.cep || prev.cep,
                street: found.street || prev.street,
                number: found.number || prev.number,
                complement: prev.complement,
                neighborhood: found.neighborhood || prev.neighborhood,
                city: found.city || prev.city,
                state: found.state || prev.state,
              }));
              setFreeform('');
            } else if (place.address && !typed) {
              setFreeform(place.address);
            }
          });
        }}
        onClose={() => setMapOpen(false)}
      />
    </>
  );
}

/**
 * O endereço da coordenada: o do sistema operacional quando existe, o público
 * quando não. Na web o nativo nem é tentado — ele só imprime um aviso e volta
 * vazio.
 */
async function describe(
  latitude: number,
  longitude: number,
): Promise<ReverseAddress | null> {
  if (Platform.OS !== 'web') {
    try {
      const [place] = await Location.reverseGeocodeAsync({
        latitude,
        longitude,
      });
      if (place?.street || place?.city) {
        return {
          cep: place.postalCode ?? '',
          street: place.street ?? place.name ?? '',
          number: place.streetNumber ?? '',
          neighborhood: place.district ?? '',
          city: place.city ?? place.subregion ?? '',
          state: place.region ?? '',
        };
      }
    } catch {
      // Reverso nativo indisponível: cai no público, logo abaixo.
    }
  }

  try {
    return await reverseGeocode(latitude, longitude);
  } catch {
    return null;
  }
}

function Shortcut({
  icon,
  label,
  onPress,
  disabled,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <PressableScale
      onPress={onPress}
      disabled={disabled}
      className="flex-1 flex-row items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/5 py-2.5"
    >
      <Ionicons name={icon} size={15} color={PRIMARY} />
      <ThemedText className="text-xs font-semibold text-primary">
        {label}
      </ThemedText>
    </PressableScale>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  keyboardType,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  keyboardType?: 'number-pad';
}) {
  return (
    <View className="gap-1">
      <ThemedText className="text-xs text-foreground/45">{label}</ThemedText>
      <Input
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        keyboardType={keyboardType}
      />
    </View>
  );
}
